import { useAvmFlow } from "./avm/useAvmFlow";
import AddressSearch from "./avm/AddressSearch";
import RegistrationGate from "./avm/RegistrationGate";
import NeedsSetupCapture from "./avm/NeedsSetupCapture";
import RevealedReport from "./avm/RevealedReport";
import AccountRevealed from "./avm/AccountRevealed";
import SavedHomeValues from "./avm/SavedHomeValues";

// Instant home-value (AVM) island for the Connect Home Value page
// (SUR-2152 / SUR-1866). Orchestrator only: composes the address search,
// the loading/processing state, the forced-registration gate, and the
// revealed result. All flow logic lives in useAvmFlow; all rendering in the
// small presentational components under ./avm.
//
// The full estimate + comps are released by the SERVER only to a registered
// visitor (GET /_avm/:id), so the gate is real, not cosmetic — this component
// renders whatever phase the server-driven flow reports.
export default function AvmEstimateWidget({
  agent_name: agentName = "your agent",
  // Agent identity for the revealed report's contact rail (SSD-23).
  agent_photo: agentPhoto,
  agent_email: agentEmail,
  agent_phone: agentPhone,
  agent_brokerage: agentBrokerage,
  cta_text: ctaText = "Find My Home Value",
  card_title: cardTitle,
  card_subtitle: cardSubtitle,
  // Trust badges under the CTA — agent-customizable strings; blanks are dropped
  // and all-blank falls back to AddressSearch's defaults.
  trust_item_1: trustItem1,
  trust_item_2: trustItem2,
  trust_item_3: trustItem3,
  // Server-owned (never a template setting): true when requesting a value also
  // enrolls the visitor in monthly update emails, so the consent line shows
  // exactly when the agent has auto-enrollment on.
  monthly_updates_consent: monthlyUpdatesConsent = false,
  // Where the revealed report's full-width breakdown is portaled (rendered in the
  // Liquid template below the hero). Defaults match home_value_report.liquid.
  report_slot_id: reportSlotId = "avm-report-slot",
  report_section_id: reportSectionId = "avm-report",
  template_key: templateKey,
  // Full home-value report page URL (account dashboard only). When present on the
  // "account" template, a revealed valuation renders a COMPACT card (value +
  // range + "View full report" link) instead of cramming the full report into
  // the narrow dashboard card — the link deep-links to this valuation.
  report_url: reportUrl,
  // Report-section visibility + order (driven by the Section Manager, SUR-2270).
  // Resolved server-side from the template's declared sections + the page's
  // overrides, passed in as booleans + an ordered key list. Default everything
  // on so an un-customized page (or one saved before sections existed) shows the
  // full report — `on()` below treats only an explicit `false` as hidden.
  show_property_details: showPropertyDetails = true,
  show_valuation_history: showValuationHistory = true,
  show_comparables: showComparables = true,
  show_public_records: showPublicRecords = true,
  // Resolved order of the report's island sub-sections; falls back to the
  // natural order when not supplied.
  section_order: sectionOrder,
  details_heading: detailsHeading = "Property details",
  history_heading: historyHeading = "Estimated value history",
  comparables_heading: comparablesHeading = "Recent comparable sales",
  deeds_heading: deedsHeading = "Sale & deed history",
  mortgages_heading: mortgagesHeading = "Mortgage history",
  // Editor live-preview (SUR-2270 / SUR-2435): the dedicated Home Value page
  // renders representative report data so its managed report sections can be
  // edited. Pages that only embed the AVM as a cross-sell render the initial
  // address card instead; expanding a full sample report there overwhelms the
  // actual page preview and makes it look like the wrong template was loaded.
  preview = false,
}) {
  const liveFlow = useAvmFlow({ templateKey, disabled: preview, monthlyUpdatesConsent });
  const previewFlow = templateKey === "home_value_report" ? PREVIEW_REPORT_FLOW : PREVIEW_SEARCH_FLOW;
  const flow = preview ? previewFlow : liveFlow;

  if (flow.phase === "needs_setup") {
    return (
      <NeedsSetupCapture
        agentName={agentName}
        leadDone={flow.leadDone}
        leadError={flow.leadError}
        onSubmit={flow.captureLead}
      />
    );
  }

  if (flow.phase === "error") {
    return (
      <Box>
        We couldn&apos;t pull your estimate right now. Please try again in a moment, or contact {agentName} directly.
      </Box>
    );
  }

  if (flow.phase === "loading") {
    return (
      <Box>
        <Spinner /> Crunching recent sales near your home…
      </Box>
    );
  }

  if (flow.phase === "locked") {
    return (
      <RegistrationGate
        agentName={agentName}
        regError={flow.regError}
        onRequestCode={flow.requestCode}
        onVerifyCode={flow.verifyCode}
      />
    );
  }

  if (flow.phase === "revealed") {
    // Account dashboard: the card is too narrow for the full report, so show a
    // compact value + "View full report" link out to the home-value page. Only
    // when there's a page to link to — otherwise fall through to the inline
    // report so nothing is lost.
    if (templateKey === "account" && reportUrl) {
      return <AccountRevealed flow={flow} reportUrl={reportUrl} />;
    }
    return (
      <RevealedReport
        flow={flow}
        agentName={agentName}
        agentPhoto={agentPhoto}
        agentEmail={agentEmail}
        agentPhone={agentPhone}
        agentBrokerage={agentBrokerage}
        showPropertyDetails={showPropertyDetails}
        showValuationHistory={showValuationHistory}
        showComparables={showComparables}
        showPublicRecords={showPublicRecords}
        sectionOrder={sectionOrder}
        detailsHeading={detailsHeading}
        historyHeading={historyHeading}
        comparablesHeading={comparablesHeading}
        deedsHeading={deedsHeading}
        mortgagesHeading={mortgagesHeading}
        reportSlotId={reportSlotId}
        reportSectionId={reportSectionId}
      />
    );
  }

  // phase === "search"
  return (
    <>
      <AddressSearch
        query={flow.query}
        suggestions={flow.suggestions}
        searching={flow.searching}
        lookupError={flow.lookupError}
        ctaText={ctaText}
        cardTitle={cardTitle}
        cardSubtitle={cardSubtitle}
        trustItems={[trustItem1, trustItem2, trustItem3].filter(Boolean)}
        consentText={monthlyUpdatesConsent ? monthlyUpdatesConsentText(agentName) : null}
        onChange={flow.onQueryChange}
        onSelect={flow.selectAddress}
      />
      <SavedHomeValues saved={flow.saved} onOpen={flow.openSaved} onDelete={flow.removeSaved} />
    </>
  );
}

// Shown under the CTA when a request enrolls the visitor in monthly updates
// (Connect::HomeValue#enroll_from_visitor_request!). Keep it accurate to what
// the server does: email only, monthly, unsubscribe link in every email.
export function monthlyUpdatesConsentText(agentName) {
  return `By requesting your home value, you agree to receive a monthly email from ${agentName} with an updated value for your home. Unsubscribe anytime.`;
}

// Representative sample report for the editor preview (SUR-2270) — shaped to
// match what useAvmFlow exposes once a real valuation reveals. Photos are https
// (CSP img-src allows it).
const PREVIEW_REPORT_FLOW = {
  phase: "revealed",
  agentEstimate: null,
  property: {
    image: "https://images.unsplash.com/photo-1568605114967-8130f3a36994?auto=format&fit=crop&w=1200&q=70",
    address: "1428 Maple Crest Dr, Austin, TX 78704",
    beds: 4,
    baths: 3,
    sqft: 2340,
    year_built: 1998,
    lot_size_acres: 0.28,
    garage_cars: 2,
    market_value: 615000,
    land_value: 180000,
    assessment_year: 2025,
    owner: "Sample owner of record",
  },
  valuation: {
    estimate: 642000,
    low: 611000,
    high: 674000,
    confidence: "High",
    history: [
      { date: "2021-06-01", value: 512000, low: 489000, high: 535000 },
      { date: "2023-06-01", value: 588000, low: 561000, high: 615000 },
      { date: "2025-06-01", value: 642000, low: 611000, high: 674000 },
    ],
  },
  comps: [
    { address: "1410 Maple Crest Dr", sold_price: 629000, beds: 4, baths: 2.5, sqft: 2210, sold_date: "2025-03-12", distance: 0.2 },
    { address: "2207 Oak Bend Ln", sold_price: 655000, beds: 4, baths: 3, sqft: 2480, sold_date: "2025-02-28", distance: 0.5 },
    { address: "908 Cedar Hollow", sold_price: 612500, beds: 3, baths: 2, sqft: 2050, sold_date: "2025-01-30", distance: 0.7 },
  ],
  deeds: [
    { date: "2018-03-14", buyer: "Sample buyer", seller: "Prior owner", price: 498000, document_type: "Warranty deed" },
  ],
  mortgages: [
    { date: "2018-03-14", lender: "Sample Mortgage Co.", loan_amount: 398400, interest_rate: 4.25, loan_term_years: 30 },
  ],
};

// Cross-sell AVM cards should look like a visitor's initial state in the page
// editor. Keep the handlers inert: a live preview is for layout/content review,
// not for starting a real valuation from the agent's authenticated session.
const noop = () => {};
const PREVIEW_SEARCH_FLOW = {
  phase: "search",
  query: "",
  suggestions: [],
  searching: false,
  saved: [],
  onQueryChange: noop,
  selectAddress: noop,
  openSaved: noop,
  removeSaved: noop,
};

function Box({ children }) {
  return (
    <div style={{ marginTop: "1rem", padding: "0.95rem 1.1rem", borderRadius: 12, background: "rgba(255,255,255,0.14)" }}>
      {children}
    </div>
  );
}

function Spinner() {
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-block",
        width: 14,
        height: 14,
        marginRight: 8,
        verticalAlign: "middle",
        border: "2px solid rgba(255,255,255,0.5)",
        borderTopColor: "#fff",
        borderRadius: "50%",
        animation: "ss-spin 0.7s linear infinite",
      }}
    />
  );
}
